import { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/Pathways/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES, preparePayload } from 'pages/Pathways/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import LoadingIndicator from 'components/LoadingIndicator';
import ComponentPreview from 'components/PreviewWrapper';
import parseDefaultValues from 'pages/Pathways/Form/parseDefaultValues';
import { useGetVenue, useGetVenuePathway } from 'api/venues';
import FormWrapper from 'components/FormWrapper';

function VenuePathwayEdit() {
  const { id = '', venueId = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const backPath = useMemo(
    () =>
      generatePath(ROUTES.venues.pathways.base, {
        id: venueId,
      }),
    [venueId],
  );

  const methods = useForm<Pathway>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError, reset } = methods;

  const { data: { data: venue } = {}, isFetching: isFetchingVenue } = useGetVenue(+venueId);

  const { data: { data: pathway } = {}, isFetching: isFetchingPathway } = useGetVenuePathway(+venueId, +id);

  const { mutate: editPathway, isLoading: isEditing } = useMutation(
    (data: Pathway) =>
      API.put(APIRoutes.venues.pathways.byId(+venueId, +id), {
        pathway: preparePayload(data),
      }),
    {
      mutationKey: 'venuePathwaysMutation',
      onSuccess: () => {
        toast.success('Zmiany w ścieżce zostały zastosowane');
        navigate(backPath);
        void queryClient.invalidateQueries('venuePathways');
      },
      onError: ({ errors }) => {
        setFormErrors(setError, errors);
        toast.error('Wystąpił problem podczas edycji ścieżki');
      },
    },
  );

  const onSubmit = handleSubmit((data: Pathway) => editPathway(data));

  useEffect(() => {
    if (pathway) reset({ ...DEFAULT_VALUES, ...parseDefaultValues(pathway) });
  }, [pathway, reset]);

  const isLoading = isFetchingPathway || isEditing || isFetchingVenue;

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: venue?.name ?? '',
          to: backPath,
        }}
        crumbs={[{ to: '', label: 'Edytuj ścieżkę' }]}
      />
      {isFetchingPathway && <LoadingIndicator />}
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="pathway-editor" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} id={+venueId} />
          </Box>
          <ComponentPreview kind="pathway" isEditMode />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} formId="pathway-editor" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default VenuePathwayEdit;
